import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  CreateVersionMappingInput,
  ListVersionMappingsParams,
  UpdateVersionMappingInput,
  VersionMapping,
} from './version-mappings-types';

const KEYS = {
  all: ['version-mappings'] as const,
  // Query key includes both filters so a query for (game=A, platform=ios) doesn't
  // hit the cache for a (game=A, platform=android) request.
  list: (params?: ListVersionMappingsParams) =>
    [...KEYS.all, 'list', params?.game_id ?? 'all', params?.platform ?? 'all'] as const,
};

export function useVersionMappings(params?: ListVersionMappingsParams) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: async () => {
      const r = await apiClient.get<VersionMapping[]>('/admin/version-mappings', {
        params: {
          ...(params?.game_id ? { game_id: params.game_id } : {}),
          ...(params?.platform ? { platform: params.platform } : {}),
        },
      });
      return r.data;
    },
  });
}

export function useCreateVersionMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVersionMappingInput) =>
      apiClient.post<VersionMapping>('/admin/version-mappings', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateVersionMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateVersionMappingInput }) =>
      apiClient.patch<VersionMapping>(`/admin/version-mappings/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeleteVersionMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/version-mappings/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
